// utils
import { paramCase } from 'src/utils/change-case';
import { _id, _postTitles } from 'src/_mock/assets';

// ----------------------------------------------------------------------

const MOCK_ID = _id[1];
const MOCK_TITLE = _postTitles[2];

const ROOTS = {
  AUTH: '/auth',
  AUTH_DEMO: '/auth-demo',
  DASHBOARD: '/dashboard',
  MANAGE: '/manage',
};

// ----------------------------------------------------------------------

export const paths = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
  docs: 'https://docs.minimals.cc',
  changelog: 'https://docs.minimals.cc/changelog',
  zoneUI: 'https://mui.com/store/items/zone-landing-page/',
  minimalUI: 'https://mui.com/store/items/minimal-dashboard/',
  freeUI: 'https://mui.com/store/items/minimal-dashboard-free/',
  figma:
    'https://www.figma.com/file/kAYnYYdib0aQPNKZpgJT6J/%5BPreview%5D-Minimal-Web.v5.0.0?type=design&node-id=0%3A1&t=Al4jScQq97Aly0Mn-1',
  product: {
    root: `/product`,
    checkout: `/product/checkout`,
    details: (id: string) => `/product/${id}`,
    demo: {
      details: `/product/${MOCK_ID}`,
    },
  },
  post: {
    root: `/post`,
    details: (title: string) => `/post/${paramCase(title)}`,
    demo: {
      details: `/post/${paramCase(MOCK_TITLE)}`,
    },
  },
  // AUTH
  auth: {
    amplify: {
      login: `${ROOTS.AUTH}/amplify/login`,
      verify: `${ROOTS.AUTH}/amplify/verify`,
      register: `${ROOTS.AUTH}/amplify/register`,
      newPassword: `${ROOTS.AUTH}/amplify/new-password`,
      forgotPassword: `${ROOTS.AUTH}/amplify/forgot-password`,
    },
    jwt: {
      login: `${ROOTS.AUTH}/jwt/login`,
      register: `${ROOTS.AUTH}/jwt/register`,
    },
    firebase: {
      login: `${ROOTS.AUTH}/firebase/login`,
      verify: `${ROOTS.AUTH}/firebase/verify`,
      register: `${ROOTS.AUTH}/firebase/register`,
      forgotPassword: `${ROOTS.AUTH}/firebase/forgot-password`,
    },
    auth0: {
      login: `${ROOTS.AUTH}/auth0/login`,
    },
  },
  authDemo: {
    classic: {
      login: `${ROOTS.AUTH_DEMO}/classic/login`,
      register: `${ROOTS.AUTH_DEMO}/classic/register`,
      forgotPassword: `${ROOTS.AUTH_DEMO}/classic/forgot-password`,
      newPassword: `${ROOTS.AUTH_DEMO}/classic/new-password`,
      verify: `${ROOTS.AUTH_DEMO}/classic/verify`,
    },
    modern: {
      login: `${ROOTS.AUTH_DEMO}/modern/login`,
      register: `${ROOTS.AUTH_DEMO}/modern/register`,
      forgotPassword: `${ROOTS.AUTH_DEMO}/modern/forgot-password`,
      newPassword: `${ROOTS.AUTH_DEMO}/modern/new-password`,
      verify: `${ROOTS.AUTH_DEMO}/modern/verify`,
    },
  },
  dashboard: {
    demo: `${ROOTS.DASHBOARD}/demo`,
    root: ROOTS.MANAGE,
    mail: `${ROOTS.DASHBOARD}/mail`,
    chat: `${ROOTS.DASHBOARD}/chat`,
    blank: `${ROOTS.DASHBOARD}/blank`,
    kanban: `${ROOTS.DASHBOARD}/kanban`,
    calendar: `${ROOTS.DASHBOARD}/calendar`,
    fileManager: `${ROOTS.DASHBOARD}/file-manager`,
    permission: `${ROOTS.DASHBOARD}/permission-denied`,
    general: {
      app: `${ROOTS.DASHBOARD}/app`,
      ecommerce: `${ROOTS.DASHBOARD}/ecommerce`,
      analytics: `${ROOTS.DASHBOARD}/analytics`,
      banking: `${ROOTS.DASHBOARD}/banking`,
      booking: `${ROOTS.DASHBOARD}/booking`,
      file: `${ROOTS.DASHBOARD}/file`,
      settings: {
        main: `${ROOTS.DASHBOARD}/settings/companies`,
        // info: `${ROOTS.DASHBOARD}/company/info`,
        companies: `${ROOTS.DASHBOARD}/settings/companies`,
        companyModify: (id: string) => `${ROOTS.DASHBOARD}/settings/company/${id}/modify`,
        companyNew: `${ROOTS.DASHBOARD}/settings/company/create`,
        zones: `${ROOTS.DASHBOARD}/settings/zones`,
        zoneModify: (id: string) => `${ROOTS.DASHBOARD}/settings/zone/${id}/modify`,
        zoneNew: `${ROOTS.DASHBOARD}/settings/zone/create`,
        services: `${ROOTS.DASHBOARD}/settings/services`,
        serviceModify: (id: string) => `${ROOTS.DASHBOARD}/settings/service/${id}/modify`,
        serviceNew: `${ROOTS.DASHBOARD}/settings/service/create`,
        cities: `${ROOTS.DASHBOARD}/settings/cities`,
        cityModify: (id: string) => `${ROOTS.DASHBOARD}/settings/city/${id}/modify`,
        cityNew: `${ROOTS.DASHBOARD}/settings/city/create`
      },
      chassis: {
        main: `${ROOTS.DASHBOARD}/chassis`,
        list: `${ROOTS.DASHBOARD}/chassis/list`,
        new: (tabName: string) => `${ROOTS.DASHBOARD}/chassis/new/${tabName}`,
        modify: (tabName: string, id: string) => `${ROOTS.DASHBOARD}/chassis/${id}/modify/${tabName}`,
        tabNavigator: (tabName: string) => `${ROOTS.DASHBOARD}/chassis/${tabName}`,
      },
      trucks: {
        main: `${ROOTS.DASHBOARD}/trucks`,
        list: `${ROOTS.DASHBOARD}/trucks/list`,
        new: (tabName: string) => `${ROOTS.DASHBOARD}/trucks/new/${tabName}`,
        modify: (tabName: string, id: string) => `${ROOTS.DASHBOARD}/trucks/${id}/modify/${tabName}`,
        tabNavigator: (tabName: string) => `${ROOTS.DASHBOARD}/trucks/${tabName}`,
      },
      yards: {
        main: `${ROOTS.DASHBOARD}/yards`,
      },
      yard: `${ROOTS.DASHBOARD}/yards`,
      yardLocations: `${ROOTS.DASHBOARD}/yard_locations/list`,

      driver: {
        main: `${ROOTS.DASHBOARD}/drivers`,
        list: `${ROOTS.DASHBOARD}/drivers/list`,
        tabNavigator: (tab: string) => `${ROOTS.DASHBOARD}/drivers/tabNavigator/${tab}`,
        new: (tab: string) => `${ROOTS.DASHBOARD}/drivers/new/${tab}`,
        modify: (id: string, tab: string) => `${ROOTS.DASHBOARD}/drivers/${id}/modify/${tab}`,
        generalDetails: (id: string, type: string) => `${ROOTS.DASHBOARD}/drivers/${id}/${type}/generalDetails`,
        disabilityInsurance: (id: string) => `${ROOTS.DASHBOARD}/drivers/${id}/modify/disabilityInsurance`,
        experience: (id: string) => `${ROOTS.DASHBOARD}/drivers/${id}/modify/driversExperience`,
        license: (id: string) => `${ROOTS.DASHBOARD}/drivers/${id}/modify/license`,
        cvor: (id: string) => `${ROOTS.DASHBOARD}/drivers/${id}/modify/cvor`,
        insuranceDetails: (id: string) => `${ROOTS.DASHBOARD}/drivers/${id}/modify/insuranceDetails`,
      },

      clients: {
        // using
        main: `${ROOTS.DASHBOARD}/clients`,
        list: `${ROOTS.DASHBOARD}/clients/list`,
        tabNavigator: (tab: string) => `${ROOTS.DASHBOARD}/clients/tabNavigator/${tab}`,
        create: (tab: string) => `${ROOTS.DASHBOARD}/clients/create/${tab}`,
        modify: (id: string, tab: string) => `${ROOTS.DASHBOARD}/clients/${id}/modify/${tab}`,
        // service location creation and updation
        createServiceLocation: (clientId: string) => `${ROOTS.DASHBOARD}/clients/${clientId}/serviceLocation/create`,
        modifyServiceLocation: (clientId: string, serviceLocationId: string) => `${ROOTS.DASHBOARD}/clients/${clientId}/serviceLocation/${serviceLocationId}/modify`,
        //   vendor creation and updation
        createVendorLocation: (clientId: string) => `${ROOTS.DASHBOARD}/clients/${clientId}/vendorLocation/create`,
        modifyVendorLocation: (clientId: string, vendorLocationId: string) => `${ROOTS.DASHBOARD}/clients/${clientId}/vendorLocation/${vendorLocationId}/modify`,
        // need to delete
        service: `${ROOTS.DASHBOARD}/clients/service`,
        contact: `${ROOTS.DASHBOARD}/clients/contact`,
        vendors: `${ROOTS.DASHBOARD}/vendors`,
        vendorModify: (id: string) => `${ROOTS.DASHBOARD}/vendor/${id}/modify`,
        vendorNew: `${ROOTS.DASHBOARD}/vendor/create`
      },


      shipmentmaster: {
        shiplineList: `${ROOTS.DASHBOARD}/shipmentmaster/shipline`,
        commodityList: `${ROOTS.DASHBOARD}/shipmentmaster/commodities`,
        containersizes: `${ROOTS.DASHBOARD}/shipmentmaster/containersizes`,
        runTypes: `${ROOTS.DASHBOARD}/shipmentmaster/run-types`,
      },
      shipment: {
        board: `${ROOTS.DASHBOARD}/shipment/kanban-board`,
        list: `${ROOTS.DASHBOARD}/shipment/list`,
        containers: `${ROOTS.DASHBOARD}/shipment/containers`,
        create: `${ROOTS.DASHBOARD}/shipment/create`,
        tabNavigator: (tab: string) => `${ROOTS.DASHBOARD}/shipment/tabNavigator/${tab}`,
        modify: (shipmentId: string, tab: string) => `${ROOTS.DASHBOARD}/shipment/${shipmentId}/modify/${tab}`,
      },
      payment: {
        shipmentPayment: `${ROOTS.DASHBOARD}/payments/shipment-payments`,
        driverPayout: `${ROOTS.DASHBOARD}/payments/payouts`,
        driverPayoutSummary: (payoutId: string) => `${ROOTS.DASHBOARD}/payments/payouts/${payoutId}/payoutDriverSummary`,
        driverPayoutList: (payoutId: string, driverPayoutId: string ) => `${ROOTS.DASHBOARD}/payments/payouts/${payoutId}/payoutDriverSlipListing/${driverPayoutId}`,
      },
      reports: {
        shipmentPayment: `${ROOTS.DASHBOARD}/payments/shipment-payments`,
        shipmentInvoiceReport: `${ROOTS.DASHBOARD}/reports/shipment-invoice-report`,
      },
      invoice: {
        root: `${ROOTS.DASHBOARD}/invoices`,
        new: `${ROOTS.DASHBOARD}/invoices/new`,
        details: (id: string) => `${ROOTS.DASHBOARD}/invoices/${id}`,
        edit: (id: string) => `${ROOTS.DASHBOARD}/invoices/${id}/modify`,
        emails: `${ROOTS.DASHBOARD}/invoices/emails`,
      },
    },
    user: {
      // hsl admin routes start 
      // module: `${ROOTS.DASHBOARD}/user/module/list`,
      // group: `${ROOTS.DASHBOARD}/user/group/list`,

      // users: `${ROOTS.DASHBOARD}/user/list`,
      // newUser: `${ROOTS.DASHBOARD}/user/create`,
      // modifyUserRole: (id: string) => `${ROOTS.DASHBOARD}/user/usersList/${id}/modify`,
      // viewUserRole: (id: string) => `${ROOTS.DASHBOARD}/user/usersList/${id}/view`,
      // hsl admin routes end 

      root: `${ROOTS.DASHBOARD}/user`,
      new: `${ROOTS.DASHBOARD}/user/new`,
      list: `${ROOTS.DASHBOARD}/user/list`,
      cards: `${ROOTS.DASHBOARD}/user/cards`,
      profile: `${ROOTS.DASHBOARD}/user/profile`,
      account: `${ROOTS.DASHBOARD}/user/account`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/user/${id}/edit`,
      demo: {
        edit: `${ROOTS.DASHBOARD}/user/${MOCK_ID}/edit`,
      },
    },
    group: {
      list: `${ROOTS.DASHBOARD}/group/list`,
      modifyPermissions: (id: string) => `${ROOTS.DASHBOARD}/group/${id}/modifyPermissions`,
      groupPermission: (id: string) => `${ROOTS.DASHBOARD}/group/permissions/${id}`
    },
    module: {
      list: `${ROOTS.DASHBOARD}/module/list`,
      new: `${ROOTS.DASHBOARD}/group/new`,

    },
    product: {
      root: `${ROOTS.DASHBOARD}/product`,
      new: `${ROOTS.DASHBOARD}/product/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/product/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/product/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/product/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/product/${MOCK_ID}/edit`,
      },
    },
    invoice: {
      root: `${ROOTS.DASHBOARD}/invoices`,
      new: `${ROOTS.DASHBOARD}/invoices/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/invoices/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/invoices/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/invoices/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/invoices/${MOCK_ID}/edit`,
      },
    },
    post: {
      root: `${ROOTS.DASHBOARD}/post`,
      new: `${ROOTS.DASHBOARD}/post/new`,
      details: (title: string) => `${ROOTS.DASHBOARD}/post/${paramCase(title)}`,
      edit: (title: string) => `${ROOTS.DASHBOARD}/post/${paramCase(title)}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/post/${paramCase(MOCK_TITLE)}`,
        edit: `${ROOTS.DASHBOARD}/post/${paramCase(MOCK_TITLE)}/edit`,
      },
    },
    order: {
      root: `${ROOTS.DASHBOARD}/order`,
      details: (id: string) => `${ROOTS.DASHBOARD}/order/${id}`,
      demo: {
        details: `${ROOTS.DASHBOARD}/order/${MOCK_ID}`,
      },
    },
    job: {
      root: `${ROOTS.DASHBOARD}/job`,
      new: `${ROOTS.DASHBOARD}/job/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/job/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/job/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/job/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/job/${MOCK_ID}/edit`,
      },
    },
    tour: {
      root: `${ROOTS.DASHBOARD}/tour`,
      new: `${ROOTS.DASHBOARD}/tour/new`,
      details: (id: string) => `${ROOTS.DASHBOARD}/tour/${id}`,
      edit: (id: string) => `${ROOTS.DASHBOARD}/tour/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}/edit`,
      },
    },
  },

  manage: {
    root: `${ROOTS.MANAGE}`,
    user: {
      main: `${ROOTS.MANAGE}/user`,
      list: `${ROOTS.MANAGE}/user`,
      new: `${ROOTS.MANAGE}/user/new`,      
      cards: `${ROOTS.MANAGE}/user/cards`,
      profile: `${ROOTS.MANAGE}/user/profile`,
      account: `${ROOTS.MANAGE}/user/account`,
      edit: (id: string) => `${ROOTS.MANAGE}/user/${id}/edit`,
      change_password: `${ROOTS.MANAGE}/user/change_password`,
      demo: {
        edit: `${ROOTS.MANAGE}/user/${MOCK_ID}/edit`,
      },
    },
    group: {
      main: `${ROOTS.MANAGE}/group`,
      list: `${ROOTS.MANAGE}/group`,
      modifyPermissions: (id: string) => `${ROOTS.MANAGE}/group/${id}/modifyPermissions`,
      groupPermission: (id: string) => `${ROOTS.MANAGE}/group/permissions/${id}`
    },
    module: {
      main: `${ROOTS.MANAGE}/module`,
      list: `${ROOTS.MANAGE}/module`,
      new: `${ROOTS.MANAGE}/module/new`,
    },
  },
};
